import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useUpdateProfileMutation } from '../store/slices/apiSlice';
import { updateUser, selectCurrentUser } from '../store/slices/authSlice';
import { Toast } from '../components/Toast';

function Onboarding() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  const [formData, setFormData] = useState({
    userType: '',
    aiInterest: '',
    expertise: '',
    purpose: ''
  });

  const [errors, setErrors] = useState({});

  const userTypeOptions = [
    'Học sinh/Sinh viên',
    'Giáo viên/Giảng viên',
    'Nhà nghiên cứu',
    'Developer',
    'Designer',
    'Marketer',
    'Content Creator',
    'Doanh nghiệp',
    'Khác'
  ];

  const expertiseOptions = [
    { group: 'Công nghệ thông tin', items: ['Lập trình phần mềm', 'An ninh mạng', 'Khoa học dữ liệu', 'Trí tuệ nhân tạo', 'Quản trị hệ thống', 'Phát triển Web/Mobile', 'DevOps', 'Blockchain'] },
    { group: 'Kinh tế - Kinh doanh', items: ['Quản trị kinh doanh', 'Marketing', 'Tài chính - Ngân hàng', 'Kế toán - Kiểm toán', 'Thương mại điện tử', 'Kinh tế quốc tế', 'Bất động sản', 'Logistics - Chuỗi cung ứng'] },
    { group: 'Kỹ thuật', items: ['Kỹ thuật điện - Điện tử', 'Kỹ thuật cơ khí', 'Kỹ thuật xây dựng', 'Kỹ thuật hóa học', 'Kỹ thuật ô tô', 'Kỹ thuật môi trường', 'Kỹ thuật công nghiệp'] },
    { group: 'Y tế - Sức khỏe', items: ['Y khoa', 'Dược học', 'Điều dưỡng', 'Y tế công cộng', 'Dinh dưỡng', 'Kỹ thuật y sinh'] },
    { group: 'Giáo dục - Đào tạo', items: ['Sư phạm', 'Giáo dục mầm non', 'Giáo dục tiểu học', 'Giáo dục đặc biệt', 'Quản lý giáo dục'] },
    { group: 'Khoa học tự nhiên', items: ['Toán học', 'Vật lý', 'Hóa học', 'Sinh học', 'Địa lý', 'Môi trường'] },
    { group: 'Khoa học xã hội', items: ['Luật', 'Tâm lý học', 'Xã hội học', 'Quan hệ quốc tế', 'Chính trị học', 'Công tác xã hội'] },
    { group: 'Nghệ thuật - Thiết kế', items: ['Thiết kế đồ họa', 'Thiết kế nội thất', 'Thiết kế thời trang', 'Kiến trúc', 'Mỹ thuật', 'Nhiếp ảnh', 'Điện ảnh - Truyền hình', 'Âm nhạc'] },
    { group: 'Truyền thông - Báo chí', items: ['Báo chí', 'Quan hệ công chúng', 'Truyền thông đa phương tiện', 'Quảng cáo', 'Content Creator'] },
    { group: 'Ngôn ngữ - Văn hóa', items: ['Ngôn ngữ Anh', 'Ngôn ngữ Trung', 'Ngôn ngữ Nhật', 'Ngôn ngữ Hàn', 'Biên - Phiên dịch', 'Văn học', 'Lịch sử'] },
    { group: 'Nông - Lâm - Ngư nghiệp', items: ['Nông học', 'Lâm nghiệp', 'Thủy sản', 'Thú y', 'Công nghệ thực phẩm'] },
    { group: 'Du lịch - Khách sạn', items: ['Quản trị khách sạn', 'Quản trị du lịch', 'Hướng dẫn viên du lịch'] },
    { group: 'Khác', items: ['Thể dục thể thao', 'Quản lý nhà nước', 'Quốc phòng - An ninh', 'Khác'] }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.userType) {
      newErrors.userType = 'Vui lòng chọn vai trò của bạn';
    }

    if (!formData.aiInterest || formData.aiInterest.trim().length < 10) {
      newErrors.aiInterest = 'Vui lòng mô tả chi tiết hơn (tối thiểu 10 ký tự)';
    }

    if (!formData.expertise) {
      newErrors.expertise = 'Vui lòng chọn chuyên môn của bạn';
    }

    if (!formData.purpose || formData.purpose.trim().length < 10) {
      newErrors.purpose = 'Vui lòng mô tả chi tiết hơn (tối thiểu 10 ký tự)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Toast.error('Vui lòng kiểm tra lại thông tin!');
      return;
    }

    try {
      const result = await updateProfile({ 
        ...formData, 
        onboardingCompleted: true 
      }).unwrap();
      dispatch(updateUser(result));
      Toast.success('Hoàn tất thiết lập!');
      setTimeout(() => {
        navigate('/');
      }, 500);
    } catch (error) {
      Toast.error('Không thể cập nhật hồ sơ');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-4 py-8">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in-up">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="material-symbols-rounded text-white text-5xl">person_add</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
            Chào mừng, {currentUser?.displayName}! 👋
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            Vui lòng hoàn thiện thông tin để chúng tôi hiểu bạn hơn
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 animate-scale-in">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Question 1: Bạn là ai? */}
            <div className="animate-fade-in-up stagger-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                1. Bạn là ai? <span className="text-red-500">*</span>
              </label>
              <select
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-4 py-3 border-2 rounded-xl transition-all focus:outline-none ${
                  errors.userType
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <option value="">-- Chọn vai trò --</option>
                {userTypeOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {errors.userType && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <span className="material-symbols-rounded text-[16px]">error</span>
                  {errors.userType}
                </p>
              )}
            </div>

            {/* Question 2: Bạn hứng thú gì ở AI? */}
            <div className="animate-fade-in-up stagger-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                2. Bạn hứng thú gì ở AI? <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="aiInterest"
                value={formData.aiInterest}
                onChange={handleChange}
                disabled={isLoading}
                maxLength={200}
                placeholder="Ví dụ: Tạo nội dung, phân tích dữ liệu, tự động hóa công việc..."
                className={`w-full px-4 py-3 border-2 rounded-xl transition-all focus:outline-none ${
                  errors.aiInterest
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.aiInterest ? (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <span className="material-symbols-rounded text-[16px]">error</span>
                    {errors.aiInterest}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">Tối thiểu 10 ký tự</p>
                )}
                <p className="text-xs text-gray-500">{formData.aiInterest.length}/200</p>
              </div>
            </div>

            {/* Question 3: Chuyên môn của bạn là gì? */}
            <div className="animate-fade-in-up stagger-3">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                3. Chuyên môn của bạn là gì? <span className="text-red-500">*</span>
              </label>
              <select
                name="expertise"
                value={formData.expertise}
                onChange={handleChange}
                disabled={isLoading}
                className={`w-full px-4 py-3 border-2 rounded-xl transition-all focus:outline-none ${
                  errors.expertise
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <option value="">-- Chọn chuyên ngành --</option>
                {expertiseOptions.map((group) => (
                  <optgroup key={group.group} label={group.group}>
                    {group.items.map((item) => (
                      <option key={item} value={item}>{item}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {errors.expertise && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <span className="material-symbols-rounded text-[16px]">error</span>
                  {errors.expertise}
                </p>
              )}
            </div>

            {/* Question 4: Mục đích dùng web là gì? */}
            <div className="animate-fade-in-up stagger-4">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                4. Mục đích dùng web là gì? <span className="text-red-500">*</span>
              </label>
              <textarea
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                disabled={isLoading}
                maxLength={300}
                rows={4}
                placeholder="Ví dụ: Tìm kiếm prompt chất lượng, chia sẻ kinh nghiệm, học hỏi từ cộng đồng..."
                className={`w-full px-4 py-3 border-2 rounded-xl transition-all focus:outline-none resize-none ${
                  errors.purpose
                    ? 'border-red-500 focus:border-red-600'
                    : 'border-gray-200 dark:border-gray-600 focus:border-primary-500'
                } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.purpose ? (
                  <p className="text-sm text-red-600 flex items-center gap-1">
                    <span className="material-symbols-rounded text-[16px]">error</span>
                    {errors.purpose}
                  </p>
                ) : (
                  <p className="text-xs text-gray-500">Tối thiểu 10 ký tự</p>
                )}
                <p className="text-xs text-gray-500">{formData.purpose.length}/300</p>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-4 rounded-xl font-semibold text-white transition-all duration-300 flex items-center justify-center gap-2 ${
                isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 shadow-lg hover:shadow-xl'
              } animate-fade-in-up stagger-5`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Đang xử lý...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-rounded text-[24px]">check_circle</span>
                  <span>Hoàn tất</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer Info */}
        <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400 animate-fade-in flex items-center justify-center gap-2">
          <span className="material-symbols-rounded text-[18px]">info</span>
          <p>Thông tin này giúp chúng tôi cải thiện trải nghiệm của bạn</p>
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
